import * as React from "react";
import { useUserLanguage } from "@/lib/i18n";
import { useT } from "@/lib/i18n/use-t";
import { Label } from "../label";
import { Textarea } from "../textarea";
import { csvLineErrorMessage } from "./csv-parse-error";
import { T } from "./csv-text-parser.i18n";
import { type CsvDataPoint, parseCsvToTimeSeriesStrict } from "./csv-utils";

/**
 * CsvTextParser - A controlled textarea for parsing CSV time series data.
 *
 * @description
 * Provides a textarea input that validates and parses CSV-formatted data
 * where each line contains a date;value pair. Shows inline validation errors.
 *
 * @example
 * ```tsx
 * import { CsvTextParser, CsvDataPoint } from "@/components/ui/csv-parser";
 *
 * const [csvText, setCsvText] = useState("");
 * const [parsedData, setParsedData] = useState<CsvDataPoint[] | null>(null);
 *
 * <CsvTextParser
 *   id="time-series"
 *   label="Time Series Data"
 *   value={csvText}
 *   onChange={setCsvText}
 *   onValidationChange={(isValid, data) => {
 *     if (isValid) setParsedData(data);
 *   }}
 *   placeholder="2026-01-01;100.5&#10;2026-01-02;101.2"
 *   required
 * />
 * ```
 *
 * @example Expected CSV format:
 * ```
 * 2026-01-01;100.5
 * 2026-01-02;101.2
 * 2026-01-03;99.8
 * ```
 */
type CsvTextParserProps = {
  /** HTML id attribute for the textarea element */
  id?: string;
  /** Label text displayed above the textarea */
  label?: string;
  /** Current CSV text value (controlled) */
  value: string;
  /** Callback fired when the text value changes */
  onChange: (value: string) => void;
  /** Callback fired when validation state changes, provides parsed data in JSON format if valid */
  onValidationChange?: (isValid: boolean, data: CsvDataPoint[] | null) => void;
  /** Placeholder text shown when textarea is empty */
  placeholder?: string;
  /** Number of visible text rows (default: 5) */
  rows?: number;
  /** Shows a required indicator (*) next to the label */
  required?: boolean;
  /** Custom error message shown when CSV format is invalid */
  errorMessage?: string;
  /** Optional helper text describing the expected input */
  helpText?: string;
  /** Disables the textarea */
  disabled?: boolean;
};

/**
 * CsvTextParser Component
 *
 * A controlled textarea that validates CSV time series input in real-time.
 * Displays inline error messages when the format is invalid.
 *
 * @see {@link CsvTextParserProps} for available props
 * @see {@link parseCsvToTimeSeriesStrict} to parse CSV strings manually
 * @see {@link dataPointsToCsv} to convert data back to CSV format
 */
function CsvTextParser({
  id,
  label,
  value,
  onChange,
  onValidationChange,
  placeholder,
  rows = 5,
  required,
  errorMessage,
  helpText,
  disabled,
}: CsvTextParserProps) {
  const t = useT(T);
  const { userLanguage } = useUserLanguage();
  const [error, setError] = React.useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (!newValue.trim()) {
      setError(null);
      onValidationChange?.(true, null);
      return;
    }
    const result = parseCsvToTimeSeriesStrict(newValue, { headerMode: "optional" });
    if (result.success) {
      setError(null);
      onValidationChange?.(true, result.data);
    } else {
      setError(
        `${errorMessage ?? t.textFormatError}\n${csvLineErrorMessage(result.error, result.lineNumber, userLanguage)}`,
      );
      onValidationChange?.(false, null);
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={id}>
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}
      <Textarea
        id={id}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
      />
      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
      {error && <p className="text-sm text-destructive whitespace-pre-line">{error}</p>}
    </div>
  );
}

export { CsvTextParser };
